import React, { useContext } from 'react'
import { useHistory } from 'react-router-dom'
import { ChatContextConsumer } from '@deal/chat-firebase'
import { LoggedInCartConsumer } from '#src/containers/Cart'
import { IdentityContext } from '#src/containers/Identity'
import { NavItem } from '../../Desktop/TopLevelNavItem'
import { useDepartmentContext } from '#src/context/department'
import { useNavigationContext } from '#src/components/Header/StandardHeader/NavigationContext'
import NavItemMenuOption from '../TopLevelNavItemMenuOption'
import ReleaseConsumerIdentityLink from '#src/components/Header/common/ReleaseConsumerIdentityLink'
import AccountIcon from '#src/components/Header/StandardHeader/icons/account.svg'
import BadgeIcon from '#src/components/Header/StandardHeader/icons/badge.svg'
import LoudSpeakerIcon from '#src/components/Header/StandardHeader/icons/loud-speaker.svg'
import OrdersIcon from '#src/components/Header/StandardHeader/icons/orders.svg'
import CartIcon from '#src/components/Header/StandardHeader/icons/cart.svg'
import GiftCardIcon from '#src/components/Header/StandardHeader/icons/gift-card.svg'
import ArrowIcon from '#src/components/Header/StandardHeader/icons/arrow.svg'
import InboxIcon from '#src/components/Header/StandardHeader/icons/inbox.svg'
import SearchIcon from '#src/components/Header/StandardHeader/icons/search.svg'
import CurationIcon from '#src/components/Header/StandardHeader/icons/curation.svg'
import SupportIcon from '#src/components/Header/StandardHeader/icons/support.svg'
import LogOutIcon from '#src/components/Header/StandardHeader/icons/log-out.svg'
import styles from './styles.css'
import useBreakpoint from '#src/hooks/useBreakpoint'
import { useExperiment } from '@deal/experiment-js'

const LoggedIn: React.FC = () => {
  const history = useHistory()
  const navContext = useNavigationContext()
  const departmentContext = useDepartmentContext()
  const { myself } = useContext(IdentityContext)
  const isImpersonating = myself && myself.user.id !== myself.realUser.id
  const isSmallerThanXlg = !useBreakpoint('xlg')

  // if the name is longer than 8 chars and the screen is near the lg breakpoint,
  // it smushes the header, so we show 'Account' instead
  const userDisplayName = myself?.user.firstName
    ? isSmallerThanXlg && myself.user.firstName.length > 8
      ? 'Account'
      : myself.user.firstName
    : 'Account'

  const { result: consumerGiftCardResult } = useExperiment({
    experiment: 'consumer-gift-card',
    defaultTreatment: 'off'
  })

  return (
    <ul className={styles.navList}>
      {departmentContext.department && (
        <NavItem
          onStaticAction={() => {
            navContext?.onToggleIsSearchVisible(true)
          }}
          testId="desktop-search"
          key="desktop-search"
        >
          <SearchIcon className={styles.navItemIcon} />
          <span className={styles.navItemLabel}>Search</span>
        </NavItem>
      )}
      <NavItem to="/inbox" testId="desktop-inbox" key="desktop-inbox">
        <ChatContextConsumer>
          {({ unreadMessageCount }) => (
            <>
              {!!unreadMessageCount && unreadMessageCount > 0 && (
                <div className={styles.badge}>{unreadMessageCount}</div>
              )}

              <InboxIcon className={styles.navItemIcon} />
              <span className={styles.navItemLabel}>Inbox</span>
            </>
          )}
        </ChatContextConsumer>
      </NavItem>
      <NavItem to="/curations" testId="desktop-curations" key="desktop-curations">
        <CurationIcon className={styles.navItemIcon} />
        <span className={styles.navItemLabel}>Curations</span>
      </NavItem>
      <NavItem
        to={{
          ...history.location,
          hash: '#cart'
        }}
        isActive={(_match, location) => location.hash === '#cart'}
        key="desktop-cart"
        testId="desktop-cart"
      >
        <LoggedInCartConsumer>
          {loggedInCartContext => {
            const lineItemCount = loggedInCartContext?.order?.itemsCount
            return (
              <>
                {!!lineItemCount && <div className={styles.badge}>{lineItemCount}</div>}

                <CartIcon className={styles.navItemIcon} />
                <span className={styles.navItemLabel}>Cart</span>
              </>
            )
          }}
        </LoggedInCartConsumer>
      </NavItem>

      <NavItem
        to="/account/personal-information"
        menuOptions={
          <>
            <NavItemMenuOption to="/account/personal-information">
              <BadgeIcon className={styles.navItemMenuIcon} />
              <span>My account</span>
            </NavItemMenuOption>

            <NavItemMenuOption to="/account/notification-settings">
              <LoudSpeakerIcon className={styles.navItemMenuIcon} />
              <span>Notification settings</span>
            </NavItemMenuOption>

            <NavItemMenuOption to="/account/orders">
              <OrdersIcon className={styles.navItemMenuIcon} />
              <span>My orders</span>
            </NavItemMenuOption>

            {consumerGiftCardResult === 'on' && (
              <NavItemMenuOption to="/account/gift-cards">
                <GiftCardIcon className={styles.navItemMenuIcon} />
                <span>Gift cards</span>
              </NavItemMenuOption>
            )}

            <NavItemMenuOption to="/support">
              <SupportIcon className={styles.navItemMenuIcon} />
              <span>Help center</span>
            </NavItemMenuOption>

            {isImpersonating && (
              <NavItemMenuOption>
                <ReleaseConsumerIdentityLink>
                  <AccountIcon className={styles.navItemMenuIcon} />
                  <span>Back to "{myself?.realUser.firstName}"</span>
                </ReleaseConsumerIdentityLink>
              </NavItemMenuOption>
            )}

            <NavItemMenuOption to="/auth/logout" rel="nofollow">
              <LogOutIcon className={styles.navItemMenuIcon} />
              <span>Sign out</span>
            </NavItemMenuOption>
          </>
        }
      >
        <AccountIcon className={styles.navItemIcon} />
        <span className={styles.navItemLabel}>
          {userDisplayName}
          {isImpersonating && ` (${myself?.realUser.firstName})`}
        </span>
        <ArrowIcon className={styles.arrowIcon} />
      </NavItem>
    </ul>
  )
}

export default LoggedIn
